import { useCallback, useState, useEffect } from 'react'
import {
  getReturnsAnnextureList,
  editReturnsAnnextureList,
  getNewSectionCodes,
} from '../api/returnsStep'
import { returnsAnnexureRowSchema, RETURNS_ANNEXURE_COLUMNS, RETURNS_ANNEXURE_COLUMNS_27Q } from '../constants/returnsStep'
import { RETURNS_ANNEXURE_COLUMNS_140, returnsAnnexureRowSchema140 } from '../constants/returnsNewFormAnnexure'
import {
  RETURNS_ANNEXURE_COLUMNS_144,
  returnsAnnexureRowSchema144,
  RETURNS_ANNEXURE_COLUMNS_27Q_NEW,
  returnsAnnexureRowSchema27QNew,
} from '../constants/returnsNewFormAnnexure144'
import { isNewFormVersion } from '../utils/formVersionUtils'

/**
 * Returns the appropriate annexure columns based on form version and type.
 * @param {string} financialYear - e.g., '2026-27'
 * @param {string} formType - e.g., '26Q', '27Q', '140', '144'
 * @param {Array} sectionCodeOptions - options fetched from NEW_SECTION_CODE API
 * @returns {Array} column definitions
 */
export const getAnnexureColumns = (financialYear, formType, sectionCodeOptions = []) => {
  console.log(financialYear, formType,"financialYear, formType")
  if (!isNewFormVersion(financialYear)) {
    return formType === '27Q' ? RETURNS_ANNEXURE_COLUMNS_27Q : RETURNS_ANNEXURE_COLUMNS
  }

  let columns
  if (formType === '144') {
    columns = RETURNS_ANNEXURE_COLUMNS_144
  } else if (formType === '27Q') {
    columns = RETURNS_ANNEXURE_COLUMNS_27Q_NEW
  } else {
    columns = RETURNS_ANNEXURE_COLUMNS_140
  }

  // Inject dynamically fetched section code options for new forms
  if (sectionCodeOptions.length) {
    return columns.map(col =>
      col.id === 'sectionCode' ? { ...col, options: sectionCodeOptions } : col
    )
  }

  return columns
}

export const useAnnexture = ({
  tanMasterUuid,
  defaultSearchCOList,
  showSnackbar,
}) => {
  const [annextureData, setAnnextureData] = useState([])
  const [editingData, setEditingData] = useState({})
  const [selectedRows, setSelectedRows] = useState([])

  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [totalItems, setTotalItems] = useState(0)
  const [loadingAnnexture,setLoadingAnnexture] = useState(false)
  const [sectionCodeOptions, setSectionCodeOptions] = useState([])

  /* ================= FETCH SECTION CODES ================= */

  useEffect(() => {
    const fetchSectionCodes = async () => {
      try {
        const resp = await getNewSectionCodes()
        const codes = resp?.data?.data
        if (codes && Array.isArray(codes)) {
          const options = codes.map((code, index) => ({
            id: index + 1,
            label: code,
            value: code,
          }))
          setSectionCodeOptions(options)
        }
      } catch {
        // silently fail — dropdown will remain empty
      }
    }
    fetchSectionCodes()
  }, [])

  /* ================= FETCH ================= */

  const fetchAnnextureData = useCallback(
    async ({ tanMasterUuid, defaultSearchCOList, currentPage = 1, itemsPerPage = 100 }) => {
      const { entityTan, entityName, returnType, formType, quarter, financialYear } =
        defaultSearchCOList || {}
      setLoadingAnnexture(true)

      const payload = {
        pageFilter: {
          includeCount: true,
          currentPage,
          sortField: 'createdDate',
          sortOrder: 'DESC',
          pageSize: itemsPerPage,
        },
        tanUuid: tanMasterUuid,
        entityTAN: entityTan,
        entityName,
        returnType: returnType?.toUpperCase(),
        formType: formType,
        quarter,
        financialYear: financialYear,
      }

      const resp = await getReturnsAnnextureList(payload)

      if(!resp?.success) {
        const errorMessage = resp?.error || 'Failed to fetch annexure data'
        showSnackbar(errorMessage, 'error')
        return
      }

      setAnnextureData(resp?.data?.content || [])
      setTotalItems(resp?.data?.totalElements || 0)
      setLoadingAnnexture(false)
    },
    [tanMasterUuid, defaultSearchCOList, currentPage, itemsPerPage]
  )

  /* ================= EDIT ================= */

  const handleFieldChange = (uuid, field, value) => {
    setEditingData(prev => ({
      ...prev,
      [uuid]: {
        ...prev[uuid],
        [field]: value,
      },
    }))
  }

  const clearEdits = () => setEditingData({})

  const buildSavePayload = () => {
    const entries = Object.entries(editingData)

    if (!entries.length) {
      return { success: true, payload: [] }
    }

    const payload = []
    const errors = []

    // Determine which schema to use based on form version and type
    const financialYear = defaultSearchCOList?.financialYear
    const formType = defaultSearchCOList?.formType
    const useNewSchema = isNewFormVersion(financialYear)

    let schema = returnsAnnexureRowSchema
    if (useNewSchema) {
      if (formType === '144') {
        schema = returnsAnnexureRowSchema144
      } else if (formType === '27Q') {
        schema = returnsAnnexureRowSchema27QNew
      } else {
        schema = returnsAnnexureRowSchema140
      }
    }

    for (const [uuid, editedFields] of entries) {
      const row = annextureData.find(r => String(r.uuid) === String(uuid))

      if (!row) {
        errors.push(`Edited row not found for uuid ${uuid}`)
        continue
      }

      const rowIndex = annextureData.findIndex(
        r => String(r.uuid) === String(uuid)
      )

      const mergedRow = { ...row, ...editedFields }

      const normalized = useNewSchema
        ? {
            ...mergedRow,
            uuid: mergedRow.uuid,
            serialNo: mergedRow.serialNo,
            challanSerialNo: String(mergedRow.challanSerialNo ?? ''),
            bsrCode: String(mergedRow.bsrCode ?? ''),
            dateOfTaxDeposited: String(mergedRow.dateOfTaxDeposited ?? ''),
            sectionCode: String(mergedRow.sectionCode ?? ''),
            totalTaxDeducted: String(mergedRow.totalTaxDeducted ?? ''),
            totalTaxDeposited: String(mergedRow.totalTaxDeposited ?? ''),
            deducteeSerialNo: String(mergedRow.deducteeSerialNo ?? ''),
            panOfDeductee: String(mergedRow.panOfDeductee ?? ''),
            dateOfDeduction: String(mergedRow.dateOfDeduction ?? ''),
            rateOfTaxDeducted: String(mergedRow.rateOfTaxDeducted ?? ''),
            amountPaidOrCredited: String(mergedRow.amountPaidOrCredited ?? ''),
            dateOfPaymentOrCredit: String(mergedRow.dateOfPaymentOrCredit ?? ''),
            deducteeName: String(mergedRow.deducteeName ?? ''),
            remarks: String(mergedRow.remarks || ''),
            certificateNo197: String(mergedRow.certificateNo197 ?? ''),
            // Form 140 specific
            uniqueIdForm121: mergedRow.uniqueIdForm121 != null ? String(mergedRow.uniqueIdForm121) : null,
            // Form 144 specific
            ...(formType === '144' || formType === '27Q'
              ? {
                  deducteeCode: String(mergedRow.deducteeCode ?? ''),
                  natureOfRemittance: String(mergedRow.natureOfRemittance ?? ''),
                  vendorEmailId: mergedRow.vendorEmailId != null ? String(mergedRow.vendorEmailId) : null,
                  vendorContactNumber: mergedRow.vendorContactNumber != null ? String(mergedRow.vendorContactNumber) : null,
                  acknowledgement15CA: mergedRow.acknowledgement15CA != null ? String(mergedRow.acknowledgement15CA) : null,
                  countryOfResidence: mergedRow.countryOfResidence != null ? String(mergedRow.countryOfResidence) : null,
                  grossingUpIndicator: mergedRow.grossingUpIndicator != null ? String(mergedRow.grossingUpIndicator) : null,
                  optingOutTaxRegime: mergedRow.optingOutTaxRegime != null ? String(mergedRow.optingOutTaxRegime) : null,
                }
              : {}),
          }
        : {
            ...mergedRow,
            uuid: mergedRow.uuid,
            serialNo: mergedRow.serialNo,
            challanSerialNo: String(mergedRow.challanSerialNo ?? ''),
            bsrCode: String(mergedRow.bsrCode ?? ''),
            dateOfTaxDeposited: String(mergedRow.dateOfTaxDeposited ?? ''),
            sectionCode: String(mergedRow.sectionCode ?? ''),
            totalTaxDeducted: String(mergedRow.totalTaxDeducted ?? ''),
            totalTaxDeposited: String(mergedRow.totalTaxDeposited ?? ''),
            deducteeSerialNo: String(mergedRow.deducteeSerialNo ?? ''),
            panOfDeductee: String(mergedRow.panOfDeductee ?? ''),
            dateOfDeduction: String(mergedRow.dateOfDeduction ?? ''),
            rateOfTaxDeducted: String(mergedRow.rateOfTaxDeducted ?? ''),
            deducteeCode: String(mergedRow.deducteeCode ?? ''),
            healthAndEducationCess: String(mergedRow.healthAndEducationCess ?? ''),
            surcharge: String(mergedRow.surcharge ?? ''),
            tds: String(mergedRow.tds ?? ''),
            amountPaidOrCredited: String(mergedRow.amountPaidOrCredited ?? ''),
            dateOfPaymentOrCredit: String(mergedRow.dateOfPaymentOrCredit ?? ''),
            deducteeName: String(mergedRow.deducteeName ?? ''),
            remarks: String(mergedRow.remarks || ''),
            deducteeReferenceNo: String(mergedRow.deducteeReferenceNo ?? ''),
            certificateNo197: String(mergedRow.certificateNo197 ?? ''),
          }

      const result = schema.safeParse(normalized)

      if (!result.success) {
        result.error.issues.forEach(issue => {
          errors.push(
            `Row ${rowIndex + 1} [${issue.path[0]}]: ${issue.message}`
          )
        })
        continue
      }

      payload.push(normalized)
    }

    if (errors.length) {
      return { success: false, errors, payload: [] }
    }

    return { success: true, payload }
  }

  const saveAnnexture = async () => {
    const { success, payload, errors } = buildSavePayload()

    if (!success) {
      errors.forEach(msg => showSnackbar(msg, 'error'))
      return false
    }

    if (!payload.length) {
      showSnackbar('No changes to save', 'info')
      return true
    }

    try {
      const response = await editReturnsAnnextureList(payload) 
      if (!response?.success || !response?.data?.success) {
        const errorMessage = response?.data?.errors?.[0]?.message || 'Annexure save failed'
        showSnackbar(errorMessage, 'error')
        return false
      }

      if (response?.data?.success) {
        showSnackbar('Annexure saved successfully', 'success')
        clearEdits()
        await fetchAnnextureData({ tanMasterUuid, defaultSearchCOList })
        return true
      } else {
        const errMessage = response?.data?.errors?.pop()?.message
        showSnackbar(errMessage || 'Annexure save failed', 'error')
        return false
      }
    } catch {
      showSnackbar('Something went wrong while saving annexture', 'error')
      return false
    }
  }

  const handlePageChange = async page => {
    setCurrentPage(page)
    fetchAnnextureData({ tanMasterUuid, defaultSearchCOList, currentPage: page, itemsPerPage })
  }

  const handleItemsPerPageChange = async newItemsPerPage => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
    fetchAnnextureData({ tanMasterUuid, defaultSearchCOList, currentPage, itemsPerPage: newItemsPerPage })
  }




  return {
    annextureData,
    selectedRows,
    totalItems,
    currentPage,
    itemsPerPage,
    editingData,
    loadingAnnexture,
    sectionCodeOptions,

    fetchAnnextureData,
    handleFieldChange,
    saveAnnexture,

    setSelectedRows,
    setCurrentPage,
    setItemsPerPage,
    clearEdits,
    handleItemsPerPageChange,
    handlePageChange
  }
}
